/**
 * Diff layer: keeps per-component GPU state keyed by serial and recomputes only what changed.
 *  - filters changed or entry model changed  -> re-evaluate the atom set (host callback, async) and rebuild every pass
 *  - one visualization changed               -> rebuild that pass only
 *  - hidden toggled                          -> visibility only
 * Visualization kinds: Atomistic, Ribbon, MolecularSurface (needs a surface provider), ResidueLabel; Interaction is
 * ignored. Until the host installs a filter evaluator every component selects all atoms of its entry.
 *
 * Ribbon builders may be synchronous (the RibbonBuilder contract in packages/state) or return a Promise (a worker
 * client); the async result is uploaded when it arrives unless the pass was rebuilt or disposed meanwhile.
 * ChainName colouring indexes chain names across every entry (MapChainNames, RenderingViewModel.Coloring.cs:274-285);
 * adding or removing an entry rebuilds the passes coloured that way.
 */
import { Group, type Object3D } from 'three';
import type { Component, Entry, FilterEvaluator, RibbonColorFn, SurfaceProvider, VisualizationModel } from '@nanome-lite/state';
import { filterEntrySerials } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { isPolymer } from '@nanome-lite/data/model';
import { RIBBON } from '@nanome-lite/look';
import type { RibbonBuildOptions, RibbonMeshBuffers } from '@nanome-lite/ribbon';
import { bondsByAtom, buildAtomistic, type AtomisticGeometry } from './atomistic';
import { computeBounds, mergeBounds, type Bounds } from './camera';
import { colorAtoms } from './coloring';
import { CylinderBatch, ImpostorMaterials, RENDER_ORDER, SphereBatch, impostorMesh } from './impostors';
import { ResidueLabelBatch, formatResidueLabel } from './labels';
import type { PickObject } from './pick-id';
import type { PickSlots } from './picking';
import { fallbackRibbonBuilder } from './ribbon-fallback';
import { MeshMaterials, RibbonMesh } from './ribbon-pass';
import { SurfaceMesh, type SurfaceBuffers } from './surface-pass';
import type { SharedUniforms } from './gl';

export type QualityTier = 'high' | 'medium' | 'low' | 'minimum';
/** Host callback shapes are the contract in packages/state/src/engine.ts. */
export type { FilterEvaluator, SurfaceProvider };

/**
 * A RibbonBuilder that may answer asynchronously (e.g. @nanome-lite/ribbon/client in a Web Worker). Every sync
 * RibbonBuilder is also one of these. `colorOf` is only valid during the call: read the colours before awaiting.
 */
export type AsyncRibbonBuilder = (model: NanomeModel, residueIndices: Uint32Array, colorOf: RibbonColorFn, options: RibbonBuildOptions) => RibbonMeshBuffers[] | Promise<RibbonMeshBuffers[]>;

function isPromiseLike<T>(v: unknown): v is PromiseLike<T> {
  return typeof v === 'object' && v !== null && typeof (v as { then?: unknown }).then === 'function';
}

/** True when the visualization colours by workspace chain name. */
export function usesChainName(vis: VisualizationModel): boolean {
  return vis.coloring.kind === 'Property' && vis.coloring.propertySource === 'ChainName';
}

/** MapChainNames: unique chain names over the entries in serial order, first appearance wins. */
export function workspaceChainNameIndex(models: Iterable<[number, NanomeModel]>): Map<string, number> {
  const out = new Map<string, number>();
  for (const [, model] of [...models].sort((a, b) => a[0] - b[0])) for (const name of model.chains.name) if (!out.has(name)) out.set(name, out.size);
  return out;
}

export interface EntryRecord {
  entry: Entry;
  model: NanomeModel;
  /** bumps whenever the model or its current frame changes */
  version: number;
  adjacency: Int32Array[] | null;
  bounds: Bounds;
}

export interface PassSet {
  kind: VisualizationModel['kind'];
  entrySerial: number;
  componentSerial: number;
  visualization: VisualizationModel;
  atomSet: Uint32Array;
  /** rgb per atomSet entry (sRGB floats) */
  colors: Float32Array;
  /** atom index -> offset into colors, -1 when absent */
  colorOffset: Int32Array;
  objects: Object3D[];
  pickObjects: Object3D[];
  /** shadow casters (light-space pass) */
  shadowObjects: Object3D[];
  slots: PickObject[];
  atomistic?: { geometry: AtomisticGeometry; bondScale: number } | undefined;
  ribbons?: RibbonMesh[] | undefined;
  surfaces?: SurfaceMesh[] | undefined;
  disposables: { dispose(): void }[];
  /** aborts a surface computation still running for this pass */
  abort?: AbortController | undefined;
  /** set when the pass is disposed (async results check it) */
  disposed?: boolean;
}

interface EntryPasses { entryVersion: number; atomSet: Uint32Array | null; generation: number; passes: (PassSet | null)[]; surfaceGeneration: number[] }
interface ComponentState { component: Component; filterKey: string; visKeys: string[]; perEntry: Map<number, EntryPasses> }

export interface ComponentManagerDeps {
  root: Group;
  pickScene: Object3D;
  /** parent for shadow casters (same root matrix as `root`) */
  shadowScene: Object3D;
  impostors: ImpostorMaterials;
  meshes: MeshMaterials;
  shared: SharedUniforms;
  slots: PickSlots;
  onChange: () => void;
  onError: (message: string) => void;
  residueLabelHeightAngstrom: number;
}

const allAtoms = (model: NanomeModel) => { const a = new Uint32Array(model.atomCount); for (let i = 0; i < a.length; i++) a[i] = i; return a; };

export class ComponentManager {
  readonly entries = new Map<number, EntryRecord>();
  private readonly components = new Map<number, ComponentState>();
  filterEvaluator: FilterEvaluator | null = null;
  surfaceProvider: SurfaceProvider | null = null;
  ribbonBuilder: AsyncRibbonBuilder = fallbackRibbonBuilder;
  quality: QualityTier = 'high';
  /** workspace-level chain-name index (MapChainNames) */
  chainNameIndex: Map<string, number> = new Map();

  constructor(private readonly deps: ComponentManagerDeps) {}

  // ---- entries -------------------------------------------------------------------------------------------------

  setEntry(entry: Entry, model: NanomeModel): void {
    const prev = this.entries.get(entry.serial);
    this.entries.set(entry.serial, { entry, model, version: (prev?.version ?? 0) + 1, adjacency: null, bounds: computeBounds(model.xyz) });
    this.updateChainNameIndex();
    this.refreshEntry(entry.serial);
  }

  removeEntry(entrySerial: number): void {
    if (!this.entries.delete(entrySerial)) return;
    for (const state of this.components.values()) {
      const ep = state.perEntry.get(entrySerial);
      if (ep) { for (const p of ep.passes) if (p) this.disposePass(p); state.perEntry.delete(entrySerial); }
    }
    this.updateChainNameIndex();
    this.deps.onChange();
  }

  /** Recompute the workspace chain-name index; rebuild ChainName-coloured passes when it changed. */
  private updateChainNameIndex(): void {
    const next = workspaceChainNameIndex([...this.entries].map(([s, r]) => [s, r.model] as [number, NanomeModel]));
    const same = next.size === this.chainNameIndex.size && [...next].every(([k, v]) => this.chainNameIndex.get(k) === v);
    if (same) return;
    this.chainNameIndex = next;
    for (const state of this.components.values()) for (const [es, ep] of state.perEntry) {
      const rec = this.entries.get(es);
      // passes of an entry whose model is being replaced are rebuilt after its filters re-evaluate
      if (!rec || !ep.atomSet || ep.entryVersion !== rec.version) continue;
      state.component.visualizations.forEach((v, i) => { if (usesChainName(v)) this.rebuildPass(state, es, ep, i); });
      for (const p of ep.passes) if (p) this.setPassVisible(p, !state.component.hidden);
    }
  }

  setCurrentModel(entrySerial: number, modelSerial: number): void {
    const rec = this.entries.get(entrySerial);
    if (!rec) return;
    rec.entry = { ...rec.entry, currentModelSerial: modelSerial };
    rec.version++;
    rec.adjacency = null;
    rec.bounds = computeBounds(rec.model.xyz);
    this.refreshEntry(entrySerial);
  }

  /** Re-run the diff for every component that references the entry. */
  private refreshEntry(entrySerial: number): void {
    const list = [...this.components.values()].map((s) => s.component);
    if (list.some((c) => this.entrySerialsOf(c).includes(entrySerial))) this.setComponents(list);
    else this.deps.onChange();
  }

  private entrySerialsOf(c: Component): number[] {
    return c.entrySerials && c.entrySerials.length > 0 ? c.entrySerials : filterEntrySerials(c.filters);
  }

  // ---- components ----------------------------------------------------------------------------------------------

  setComponents(components: Component[]): void {
    const keep = new Set<number>();
    for (const c of components) {
      keep.add(c.serial);
      let state = this.components.get(c.serial);
      if (!state) { state = { component: c, filterKey: '', visKeys: [], perEntry: new Map() }; this.components.set(c.serial, state); }
      const filterKey = JSON.stringify(c.filters);
      const visKeys = c.visualizations.map((v) => JSON.stringify(v));
      const serials = this.entrySerialsOf(c);
      for (const [es, ep] of state.perEntry) if (!serials.includes(es)) { for (const p of ep.passes) if (p) this.disposePass(p); state.perEntry.delete(es); }
      state.component = c;
      for (const es of serials) {
        const rec = this.entries.get(es);
        if (!rec) continue;
        let ep = state.perEntry.get(es);
        if (!ep) { ep = { entryVersion: -1, atomSet: null, generation: 0, passes: [], surfaceGeneration: [] }; state.perEntry.set(es, ep); }
        const needAtoms = ep.atomSet === null || state.filterKey !== filterKey || ep.entryVersion !== rec.version;
        if (needAtoms) {
          const gen = ++ep.generation;
          const epRef = ep;
          const evaluate = this.filterEvaluator ? this.filterEvaluator(c, es) : Promise.resolve(allAtoms(rec.model));
          evaluate.then((set) => {
            if (epRef.generation !== gen || this.components.get(c.serial) !== state) return;
            epRef.atomSet = set; epRef.entryVersion = rec.version;
            this.rebuildAll(state, es, epRef);
            this.deps.onChange();
          }).catch((e: unknown) => this.deps.onError(`filter evaluation failed: ${String(e)}`));
        } else {
          for (let i = 0; i < c.visualizations.length; i++) {
            if (state.visKeys[i] !== visKeys[i] || ep.passes[i] === undefined) this.rebuildPass(state, es, ep, i);
          }
          for (let i = c.visualizations.length; i < ep.passes.length; i++) { const p = ep.passes[i]; if (p) this.disposePass(p); }
          ep.passes.length = c.visualizations.length;
        }
        for (const p of ep.passes) if (p) this.setPassVisible(p, !c.hidden);
      }
      state.filterKey = filterKey;
      state.visKeys = visKeys;
    }
    for (const [serial, state] of this.components) {
      if (keep.has(serial)) continue;
      for (const ep of state.perEntry.values()) for (const p of ep.passes) if (p) this.disposePass(p);
      this.components.delete(serial);
    }
    this.deps.onChange();
  }

  private rebuildAll(state: ComponentState, entrySerial: number, ep: EntryPasses): void {
    for (const p of ep.passes) if (p) this.disposePass(p);
    ep.passes = [];
    for (let i = 0; i < state.component.visualizations.length; i++) this.rebuildPass(state, entrySerial, ep, i);
    for (const p of ep.passes) if (p) this.setPassVisible(p, !state.component.hidden);
  }

  private rebuildPass(state: ComponentState, entrySerial: number, ep: EntryPasses, visIndex: number): void {
    const old = ep.passes[visIndex];
    if (old) this.disposePass(old);
    ep.passes[visIndex] = null;
    const rec = this.entries.get(entrySerial);
    const vis = state.component.visualizations[visIndex];
    if (!rec || !vis || !ep.atomSet || !vis.visible) return;
    try {
      ep.passes[visIndex] = this.buildPass(state.component, rec, ep, vis, visIndex);
    } catch (e) {
      this.deps.onError(`failed to build ${vis.kind} for component ${state.component.serial}: ${String(e)}`);
    }
  }

  private setPassVisible(p: PassSet, visible: boolean): void {
    for (const o of p.objects) o.visible = visible;
    for (const o of p.pickObjects) o.visible = visible;
    for (const o of p.shadowObjects) o.visible = visible;
  }

  private disposePass(p: PassSet): void {
    p.abort?.abort();
    p.disposed = true;
    for (const o of p.objects) this.deps.root.remove(o);
    for (const o of p.pickObjects) this.deps.pickScene.remove(o);
    for (const o of p.shadowObjects) this.deps.shadowScene.remove(o);
    for (const s of p.slots) this.deps.slots.free(s.slot);
    for (const d of p.disposables) d.dispose();
  }

  private buildPass(component: Component, rec: EntryRecord, ep: EntryPasses, vis: VisualizationModel, visIndex: number): PassSet | null {
    const { model, entry } = rec;
    const atomSet = ep.atomSet!;
    const ctx = { entrySerial: entry.serial, modelSerial: entry.currentModelSerial, chainNameIndex: this.chainNameIndex };
    const colors = colorAtoms(model, atomSet, vis.coloring, ctx);
    const colorOffset = new Int32Array(model.atomCount).fill(-1);
    for (let k = 0; k < atomSet.length; k++) colorOffset[atomSet[k]!] = k * 3;
    const pass: PassSet = { kind: vis.kind, entrySerial: entry.serial, componentSerial: component.serial, visualization: vis, atomSet, colors, colorOffset, objects: [], pickObjects: [], shadowObjects: [], slots: [], disposables: [] };
    const addMain = (o: Object3D) => { o.matrixAutoUpdate = false; this.deps.root.add(o); pass.objects.push(o); };
    const addPick = (o: Object3D) => { o.matrixAutoUpdate = false; this.deps.pickScene.add(o); pass.pickObjects.push(o); };
    const addShadow = (o: Object3D) => { o.matrixAutoUpdate = false; this.deps.shadowScene.add(o); pass.shadowObjects.push(o); };
    const colorOfAtom = (i: number): [number, number, number] => { const o = colorOffset[i]!; return o < 0 ? [1, 1, 1] : [colors[o]!, colors[o + 1]!, colors[o + 2]!]; };

    switch (vis.kind) {
      case 'Atomistic': {
        rec.adjacency ??= bondsByAtom(model);
        const geometry = buildAtomistic(model, atomSet, colors, vis, rec.adjacency);
        pass.atomistic = { geometry, bondScale: vis.bondScale ?? 0 };
        if (geometry.spheres.count > 0) {
          const slot = this.deps.slots.allocate(entry.serial, component.serial, 'atom');
          pass.slots.push(slot);
          const b = new SphereBatch(geometry.spheres, slot.slot);
          pass.disposables.push(b);
          addMain(impostorMesh(b.geometry, this.deps.impostors.sphere.main, RENDER_ORDER.impostor));
          addPick(impostorMesh(b.geometry, this.deps.impostors.sphere.pick, RENDER_ORDER.impostor));
          addShadow(impostorMesh(b.geometry, this.deps.impostors.sphere.shadow, RENDER_ORDER.impostor));
        }
        if (geometry.cylinders.count > 0) {
          const slot = this.deps.slots.allocate(entry.serial, component.serial, 'bond');
          pass.slots.push(slot);
          const b = new CylinderBatch(geometry.cylinders, slot.slot);
          pass.disposables.push(b);
          addMain(impostorMesh(b.geometry, this.deps.impostors.cylinder.main, RENDER_ORDER.impostor));
          addPick(impostorMesh(b.geometry, this.deps.impostors.cylinder.pick, RENDER_ORDER.impostor));
          addShadow(impostorMesh(b.geometry, this.deps.impostors.cylinder.shadow, RENDER_ORDER.impostor));
        }
        return pass;
      }
      case 'Ribbon': {
        const residues = residuesOf(model, atomSet, true);
        if (residues.length === 0) return pass;
        // Ribbon colour = colour of the Cα (protein) / C5' (nucleic) atom (RenderingViewModel.Ribbon.cs:687-700)
        const colorOfResidue = (ri: number): [number, number, number] => {
          const start = model.residues.atomStart[ri]!, count = model.residues.atomCount[ri]!;
          let fallback = -1;
          for (let i = start; i < start + count; i++) {
            if (colorOffset[i]! < 0) continue;
            const n = model.atomName[i];
            if (n === 'CA' || n === "C5'") return colorOfAtom(i);
            if (fallback < 0) fallback = i;
          }
          return fallback >= 0 ? colorOfAtom(fallback) : [1, 1, 1];
        };
        const slot = this.deps.slots.allocate(entry.serial, component.serial, 'ribbon');
        pass.slots.push(slot);
        pass.ribbons = [];
        const upload = (buffers: RibbonMeshBuffers[]) => {
          for (const b of buffers) {
            if (b.vertexCount === 0) continue;
            const rm = new RibbonMesh(b, this.deps.meshes, slot.slot);
            pass.ribbons!.push(rm);
            pass.disposables.push(rm);
            addMain(rm.main); addMain(rm.hulls.hover); addMain(rm.hulls.selection); addPick(rm.pick); addShadow(rm.shadow);
          }
        };
        const result = this.ribbonBuilder(model, residues, colorOfResidue, { stepsPerResidue: RIBBON.stepsPerResidue[this.quality], reproduceSheetSmoothingQuirk: RIBBON.reproduceSheetSmoothingQuirk });
        if (isPromiseLike<RibbonMeshBuffers[]>(result)) {
          result.then((buffers) => {
            if (pass.disposed) return;
            upload(buffers);
            this.setPassVisible(pass, !(this.components.get(component.serial)?.component.hidden ?? false));
            this.deps.onChange();
          }, (e: unknown) => { if (!pass.disposed) this.deps.onError(`ribbon build failed: ${String(e)}`); });
        } else {
          upload(result);
        }
        return pass;
      }
      case 'MolecularSurface': {
        if (!this.surfaceProvider) return pass;
        const gen = (ep.surfaceGeneration[visIndex] ?? 0) + 1;
        ep.surfaceGeneration[visIndex] = gen;
        const slot = this.deps.slots.allocate(entry.serial, component.serial, 'atom');
        pass.slots.push(slot);
        const abort = new AbortController();
        pass.abort = abort;
        pass.surfaces = [];
        const alive = () => ep.surfaceGeneration[visIndex] === gen && ep.passes[visIndex] === pass && !abort.signal.aborted;
        // Meshes arrive one chain at a time (progressive) and/or all together when the provider resolves.
        const addMesh = (sb: SurfaceBuffers) => {
          if (!alive()) return;
          const sm = new SurfaceMesh(sb, colorOfAtom, this.deps.meshes, slot.slot);
          pass.surfaces!.push(sm);
          pass.disposables.push(sm);
          for (const o of sm.objects) addMain(o);
          addPick(sm.pick);
          if (sm.shadow) addShadow(sm.shadow);
          this.setPassVisible(pass, !this.components.get(component.serial)?.component.hidden);
          this.deps.onChange();
        };
        this.surfaceProvider(component, entry.serial, vis, atomSet, { onMesh: addMesh, signal: abort.signal }).then((buffers) => {
          if (buffers) for (const sb of buffers) addMesh(sb);
        }).catch((e: unknown) => {
          if (abort.signal.aborted || (e instanceof Error && e.name === 'AbortError')) return;
          this.deps.onError(`surface provider failed: ${String(e)}`);
        });
        return pass;
      }
      case 'ResidueLabel': {
        const residues = residuesOf(model, atomSet, false);
        const items = Array.from(residues).map((ri) => {
          const start = model.residues.atomStart[ri]!, count = model.residues.atomCount[ri]!;
          let cx = 0, cy = 0, cz = 0, n = 0, first = -1;
          for (let i = start; i < start + count; i++) {
            if (colorOffset[i]! < 0) continue;
            if (first < 0) first = i;
            cx += model.xyz[i * 3]!; cy += model.xyz[i * 3 + 1]!; cz += model.xyz[i * 3 + 2]!; n++;
          }
          return { text: formatResidueLabel(model.residues.name[ri] ?? '', model.residues.serial[ri]!), center: [cx / n, cy / n, cz / n] as [number, number, number], color: colorOfAtom(first) };
        });
        if (items.length === 0) return pass;
        const batch = new ResidueLabelBatch(items, this.deps.shared, this.deps.residueLabelHeightAngstrom);
        pass.disposables.push(batch);
        addMain(batch.mesh);
        return pass;
      }
      case 'Interaction':
        return null;
    }
    return null;
  }

  // ---- queries -------------------------------------------------------------------------------------------------

  *passes(): IterableIterator<PassSet> {
    for (const state of this.components.values()) for (const ep of state.perEntry.values()) for (const p of ep.passes) if (p) yield p;
  }

  componentBounds(componentSerial: number): Bounds | null {
    const state = this.components.get(componentSerial);
    if (!state) return null;
    const list: Bounds[] = [];
    for (const [es, ep] of state.perEntry) {
      const rec = this.entries.get(es);
      if (rec && ep.atomSet) list.push(computeBounds(rec.model.xyz, ep.atomSet));
    }
    return list.length ? mergeBounds(list) : null;
  }

  isHidden(componentSerial: number): boolean { return this.components.get(componentSerial)?.component.hidden ?? true; }

  /** Rebuild passes that depend on the quality tier (ribbon steps). */
  setQuality(tier: QualityTier): void {
    if (tier === this.quality) return;
    this.quality = tier;
    for (const state of this.components.values()) for (const [es, ep] of state.perEntry) {
      state.component.visualizations.forEach((v, i) => { if (v.kind === 'Ribbon') this.rebuildPass(state, es, ep, i); });
      for (const p of ep.passes) if (p) this.setPassVisible(p, !state.component.hidden);
    }
    this.deps.onChange();
  }

  /** Rebuild every pass (e.g. after a context loss). */
  rebuildEverything(): void {
    for (const state of this.components.values()) for (const [es, ep] of state.perEntry) if (ep.atomSet) this.rebuildAll(state, es, ep);
    this.deps.onChange();
  }

  dispose(): void {
    for (const state of this.components.values()) for (const ep of state.perEntry.values()) for (const p of ep.passes) if (p) this.disposePass(p);
    this.components.clear();
    this.entries.clear();
  }
}

/** Unique residues (in order) that have at least one atom in the set; polymer-only when requested. */
export function residuesOf(model: NanomeModel, atomSet: Uint32Array, polymerOnly: boolean): Uint32Array {
  const out: number[] = [];
  let last = -1;
  for (const i of atomSet) {
    const ri = model.residueIndex[i]!;
    if (ri === last) continue;
    if (polymerOnly && !isPolymer(model.residues.substructure[ri]!)) continue;
    if (out.length && out[out.length - 1] === ri) continue;
    out.push(ri); last = ri;
  }
  return Uint32Array.from(out);
}
